import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { canApplyAnnotationOnRange, getAnnotationIdsFromRange } from '@atlaskit/editor-common/utils';
import { AnnotationTypes } from '@atlaskit/adf-schema';
import { RemoveMarkStep } from '@atlaskit/editor-prosemirror/transform';
import { createAnnotationStep, getPosFromRange } from '../steps';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE, ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics';
import { getIndexMatch } from './matches-utils';
var RendererActions = /*#__PURE__*/function () {
  // Any kind of refence is allowed

  function RendererActions() {
    var initFromContext = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    _classCallCheck(this, RendererActions);
    // This is our psuedo feature flag for now
    // This module can only be used when wrapped with
    // the <RendererContext> component for now.
    _defineProperty(this, "initFromContext", false);
    this.initFromContext = initFromContext;
    this.transformer = new JSONTransformer();
  }

  //#region private
  _createClass(RendererActions, [{
    key: "_privateRegisterRenderer",
    value: function _privateRegisterRenderer(ref, doc, schema, onAnalyticsEvent) {
      if (!this.initFromContext) {
        return;
      } else if (!this.ref) {
        this.ref = ref;
      } else if (this.ref !== ref) {
        throw new Error("Renderer has already been registered! It's not allowed to re-register with another new Renderer instance.");
      }
      this.doc = doc;
      this.schema = schema;
      this.onAnalyticsEvent = onAnalyticsEvent;
    }
  }, {
    key: "_privateUnregisterRenderer",
    value: function _privateUnregisterRenderer() {
      this.doc = undefined;
      this.ref = undefined;
      this.schema = undefined;
    }

    /**
     * Validate whether we can create an annotation between two positions
     */
  }, {
    key: "_privateValidatePositionsForAnnotation",
    value: function _privateValidatePositionsForAnnotation(from, to) {
      if (!this.doc || !this.schema) {
        return false;
      }
      return canApplyAnnotationOnRange({
        from: from,
        to: to
      }, this.doc, this.schema);
    }
    //#endregion
  }, {
    key: "deleteAnnotation",
    value: function deleteAnnotation(annotationId, annotationType) {
      if (!this.doc || !this.schema || !this.schema.marks.annotation) {
        return false;
      }
      var mark = this.schema.marks.annotation.create({
        id: annotationId,
        annotationType: annotationType
      });
      var from;
      var to;
      this.doc.descendants(function (node, pos) {
        var found = mark.isInSet(node.marks);
        if (found && !from) {
          // Set both here incase it only spans one node.
          from = pos;
          to = pos + node.nodeSize;
        } else if (found && from) {
          // If the mark spans multiple nodes,
          // we'll keep setting the end until no longer found.
          to = pos + node.nodeSize;
        }
        return true;
      });
      if (from === undefined || to === undefined) {
        return false;
      }
      var step = new RemoveMarkStep(from, to, mark);
      var _step$apply = step.apply(this.doc),
        doc = _step$apply.doc,
        failed = _step$apply.failed;
      if (this.onAnalyticsEvent) {
        var payload = {
          action: ACTION.DELETED,
          actionSubject: ACTION_SUBJECT.ANNOTATION,
          actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
          eventType: EVENT_TYPE.TRACK
        };
        this.onAnalyticsEvent(payload);
      }
      if (!failed && doc) {
        return {
          step: step,
          doc: this.transformer.encode(doc)
        };
      }
      return false;
    }
  }, {
    key: "annotate",
    value: function annotate(range, annotationId, annotationType) {
      if (!this.doc || !this.schema || !this.schema.marks.annotation) {
        return false;
      }
      var pos = getPosFromRange(range);
      if (!pos) {
        return false;
      }
      var from = pos.from,
        to = pos.to;
      var validPositions = this._privateValidatePositionsForAnnotation(from, to);
      if (!validPositions) {
        return false;
      }
      return this.applyAnnotation(pos, {
        annotationId: annotationId,
        annotationType: AnnotationTypes.INLINE_COMMENT
      });
    }
  }, {
    key: "isValidAnnotationRange",
    value: function isValidAnnotationRange(range) {
      if (!range) {
        return false;
      }
      var pos = getPosFromRange(range);
      if (!pos || !this.doc) {
        return false;
      }
      return this._privateValidatePositionsForAnnotation(pos.from, pos.to);
    }
  }, {
    key: "isValidAnnotationPosition",
    value: function isValidAnnotationPosition(pos) {
      if (!pos || !this.doc) {
        return false;
      }
      return this._privateValidatePositionsForAnnotation(pos.from, pos.to);
    }
  }, {
    key: "getPositionFromRange",
    value: function getPositionFromRange(range) {
      if (!this.doc || !this.schema || !range) {
        return false;
      }
      return getPosFromRange(range);
    }
  }, {
    key: "getAnnotationMarks",
    value: function getAnnotationMarks() {
      var schema = this.schema,
        doc = this.doc;
      if (!schema || !doc) {
        return [];
      }
      var annotationMarkType = schema.marks.annotation;
      if (!annotationMarkType) {
        return [];
      }
      var marks = [];
      doc.descendants(function (node) {
        var annotationsMark = node.marks.filter(function (m) {
          return m.type === annotationMarkType;
        });
        if (!annotationsMark || !annotationsMark.length) {
          return true;
        }
        marks.push.apply(marks, _toConsumableArray(annotationsMark));
        return false;
      });
      var uniqueMarks = new Map();
      marks.forEach(function (m) {
        uniqueMarks.set(m.attrs.id, m);
      });
      return Array.from(uniqueMarks.values());
    }
  }, {
    key: "getAnnotationsByPosition",
    value: function getAnnotationsByPosition(range) {
      if (!this.doc || !this.schema) {
        return [];
      }
      var pos = getPosFromRange(range);
      if (!pos || !this.doc) {
        return [];
      }
      return getAnnotationIdsFromRange(pos, this.doc, this.schema);
    }
  }, {
    key: "applyAnnotation",
    value: function applyAnnotation(pos, annotation) {
      if (!this.doc || !pos || !this.schema) {
        return false;
      }
      var from = pos.from,
        to = pos.to;
      var annotationId = annotation.annotationId,
        annotationType = annotation.annotationType;
      var step = createAnnotationStep(from, to, {
        annotationId: annotationId,
        annotationType: annotationType,
        schema: this.schema
      });
      var _step$apply2 = step.apply(this.doc),
        doc = _step$apply2.doc,
        failed = _step$apply2.failed;
      if (failed || !doc) {
        return false;
      }
      var originalSelection = doc.textBetween(from, to);
      var _getIndexMatch = getIndexMatch(this.doc, this.schema, originalSelection, from),
        numMatches = _getIndexMatch.numMatches,
        matchIndex = _getIndexMatch.matchIndex;
      return {
        step: step,
        doc: this.transformer.encode(doc),
        originalSelection: originalSelection,
        numMatches: numMatches,
        matchIndex: matchIndex
      };
    }
  }, {
    key: "generateAnnotationIndexMatch",
    value: function generateAnnotationIndexMatch(pos) {
      if (!this.doc || !pos || !this.schema) {
        return false;
      }
      var from = pos.from,
        to = pos.to;
      var originalSelection = this.doc.textBetween(from, to);
      var _getIndexMatch2 = getIndexMatch(this.doc, this.schema, originalSelection, from),
        numMatches = _getIndexMatch2.numMatches,
        matchIndex = _getIndexMatch2.matchIndex;
      return {
        originalSelection: originalSelection,
        numMatches: numMatches,
        matchIndex: matchIndex
      };
    }
  }]);
  return RendererActions;
}();
export { RendererActions as default };